import _isEqual from 'lodash/isEqual';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';

import { usePrevious } from '@sravni/react-utils';
import { getOrganizationHelpers } from '@sravni/ugc/lib/organizations/helpers';

import { type ReviewsListParams, SravniReviewsEnumsReviewRateStatus } from '../__generated__/reviews/reviewsApiTypes';
import { FILTERS_NAME, RATING_TYPES } from '../constants/filters';
import { makeQueryParamsFromFilters } from '../helpers/filters';
import { makeBannerParams, makeDecorateBannerParams } from '../helpers/makeBannerParams';
import { makeSeoParams } from '../helpers/makeSeoParams';
import { useFilters } from '../store/hooks/filters';
import { useAppDispatch } from '../store/hooks/redux';
import { useFingerprint } from '../store/hooks/users';
import type { FetchParams } from '../store/reducers/advertising';
import { fetchBanners } from '../store/reducers/advertising';
import { fetchMetadata } from '../store/reducers/metadata';
import { fetchDictionaries, fetchProductConfig } from '../store/reducers/productPopupInfo';
import { fetchRatingsWithProductInfo } from '../store/reducers/ratings';
import { fetchReviews } from '../store/reducers/reviews';
import { makeFiltersLink } from '../utils/routing';

export const useHandleFiltersChange = () => {
  const dispatch = useAppDispatch();
  const filters = useFilters();
  const fp = useFingerprint();
  const { organizationType, service, ratingType } = filters;
  const router = useRouter();
  const prevFilters = usePrevious(filters);
  const isFirstRender = useRef(true);

  const { reviewService } = getOrganizationHelpers(organizationType);

  const metadataParams = useMemo(() => makeSeoParams(filters), [filters]);

  const reviewsParams: ReviewsListParams = useMemo(
    () => ({
      ReviewObjectType: reviewService,
      NewIds: true,
      RateStatus: SravniReviewsEnumsReviewRateStatus.RateApproved,
      Tag: service?.key as ReviewsListParams['Tag'],
      fingerPrint: fp,
    }),
    [fp, reviewService, service?.key],
  );

  const bannersParams: FetchParams = useMemo(
    () => ({
      fetchingParams: makeBannerParams({ organizationType, isFirstRender: isFirstRender.current }),
      decorateParams: makeDecorateBannerParams(filters),
    }),
    [filters, organizationType],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // первый рендер
      dispatch(fetchReviews(reviewsParams));
      return;
    }

    if (_isEqual(filters, prevFilters)) return;
    // при изменении фильтров

    const newUrl = makeFiltersLink(filters);
    const params = makeQueryParamsFromFilters(filters);

    router.push({ pathname: '/list', query: params }, newUrl, { scroll: false });
    dispatch(fetchMetadata(metadataParams));
    dispatch(fetchReviews(reviewsParams));
    dispatch(fetchBanners(bannersParams));
    dispatch(fetchRatingsWithProductInfo(filters));

    if (ratingType === RATING_TYPES.Products) {
      dispatch(fetchProductConfig({ serviceKey: filters[FILTERS_NAME.Service].key }));
      dispatch(fetchDictionaries());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters, prevFilters]);
};
